// The VMS settings row has 22 fields, but only five change anything in the
// backend. The other seventeen are stored and returned while no code reads
// them — there is no VMS background job, so auto-checkout never fires and
// approval timeouts never expire a pending visit.
//
// Presenting all 22 as equals would promise rules the system does not keep, so
// they are grouped by *who* enforces them and the screen labels each group
// honestly.

export const GROUPS = [
    {
        id: 'enforced',
        title: 'Enforced by the system',
        hint: 'These change how the backend behaves.',
        tone: 'emerald',
        icon: 'mdi:shield-check-outline',
        fields: [
            {
                key: 'gate_entry_mode',
                label: 'Gate entry mode',
                type: 'enum',
                options: [
                    { value: 'APPROVAL_REQUIRED', label: 'Approval required — the resident is asked first' },
                    { value: 'DIRECT_ENTRY', label: 'Direct entry — visitors are checked in immediately' },
                ],
                hint: 'Decides the status a new visit starts in, when no pre-approval or gate pass matches.',
            },
            {
                key: 'otp_expiry_minutes',
                label: 'OTP validity',
                type: 'number',
                min: 1,
                max: 60,
                suffix: 'minutes',
                hint: 'How long a visit OTP stays usable.',
            },
            {
                key: 'notify_resident_on_visitor',
                label: 'Notify residents about visitors',
                type: 'boolean',
                hint: 'Visit pending, approved, checked in and checked out.',
            },
            {
                key: 'notify_resident_on_delivery',
                label: 'Notify residents about deliveries',
                type: 'boolean',
            },
            {
                key: 'notify_resident_on_cab',
                label: 'Notify residents about cabs',
                type: 'boolean',
            },
        ],
    },
    {
        id: 'app',
        title: 'Enforced by this app',
        hint: 'The API accepts anything here — these limits are applied by the portal’s own forms, so they hold for anyone working in this app but not for a direct API call.',
        tone: 'sky',
        icon: 'mdi:monitor-lock',
        fields: [
            {
                key: 'max_pre_approval_days',
                label: 'Longest pre-approval window',
                type: 'number',
                min: 1,
                max: 365,
                suffix: 'days',
                hint: 'Caps the date range on the pre-approval form.',
            },
            {
                key: 'max_companion_count',
                label: 'Maximum companions',
                type: 'number',
                min: 0,
                max: 50,
                suffix: 'people',
                hint: 'Caps the companion count when logging a visit.',
            },
            {
                key: 'visitor_photo_required',
                label: 'Require a visitor photo',
                type: 'boolean',
                hint: 'Makes the photo field mandatory when logging a visit.',
            },
            {
                key: 'visitor_id_proof_required',
                label: 'Require ID proof',
                type: 'boolean',
                hint: 'Makes the ID fields mandatory when registering a visitor.',
            },
            {
                key: 'allow_resident_pre_approval',
                label: 'Residents may create pre-approvals',
                type: 'boolean',
                hint: 'Hides the button. Role permissions still decide what the API allows.',
            },
            {
                key: 'allow_resident_gate_pass',
                label: 'Residents may request gate passes',
                type: 'boolean',
                hint: 'Hides the button. Role permissions still decide what the API allows.',
            },
        ],
    },
    {
        id: 'inert',
        title: 'Saved, but not active yet',
        hint: 'Stored on the record and returned by the API, but nothing reads them — there is no background job behind auto-checkout or approval timeouts. Set them if you want the intent recorded; do not rely on them.',
        tone: 'slate',
        icon: 'mdi:progress-clock',
        fields: [
            {
                key: 'pre_approval_default_verification',
                label: 'Default verification mode',
                type: 'enum',
                options: [
                    { value: 'SIMPLE', label: 'Simple' },
                    { value: 'OTP', label: 'OTP' },
                    { value: 'NONE', label: 'None' },
                ],
            },
            { key: 'auto_checkout_enabled', label: 'Auto check-out stale visits', type: 'boolean' },
            { key: 'auto_checkout_hours', label: 'Auto check-out after', type: 'number', min: 1, max: 168, suffix: 'hours' },
            { key: 'approval_timeout_minutes', label: 'Approval times out after', type: 'number', min: 1, max: 1440, suffix: 'minutes' },
            { key: 'blocked_visitor_alert', label: 'Alert on a blocked visitor', type: 'boolean' },
            { key: 'vehicle_tracking_enabled', label: 'Vehicle tracking', type: 'boolean' },
            { key: 'delivery_tracking_enabled', label: 'Delivery tracking', type: 'boolean' },
            { key: 'cab_tracking_enabled', label: 'Cab tracking', type: 'boolean' },
            { key: 'working_hours_from', label: 'Working hours from', type: 'time' },
            { key: 'working_hours_to', label: 'Working hours to', type: 'time' },
        ],
    },
];

export const ALL_FIELDS = GROUPS.flatMap(g => g.fields);

// Booleans are written as JSON booleans but come back as 0 | 1 integers, so
// the form normalises on load and again on save.
export const fromApi = (data) => {
    const form = {};
    ALL_FIELDS.forEach(f => {
        const raw = data?.[f.key];
        if (f.type === 'boolean') form[f.key] = raw === true || raw === 1;
        else if (f.type === 'time') form[f.key] = (raw || '').slice(0, 5);
        else form[f.key] = raw ?? '';
    });
    // gate_names is a JSON array, edited as one-per-line text.
    form.gate_names = Array.isArray(data?.gate_names) ? data.gate_names.join('\n') : '';
    return form;
};

export const toApi = (form, societyId) => {
    const payload = { society_id: Number(societyId) };
    ALL_FIELDS.forEach(f => {
        const v = form[f.key];
        if (f.type === 'boolean') payload[f.key] = !!v;
        else if (f.type === 'number') { if (v !== '' && v !== null) payload[f.key] = Number(v); }
        else if (v !== '' && v !== null) payload[f.key] = v;
    });
    const gates = (form.gate_names || '')
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
    payload.gate_names = gates.length > 0 ? gates : null;
    return payload;
};
